/**
 * The advisor's scoring settings (PRD §4.3 "configurable within bounds"): how many contacts Tier 1
 * may hold, the maximum issue age, and the CRM's record-type values to leave out where their firm
 * restricts outreach to prospects. Values outside their bounds are refused by the server with the
 * reason, which is shown here. Saving rescores the book.
 *
 * The number boxes are read when the advisor leaves them rather than on every keystroke, so a number
 * can be cleared and retyped; an emptied box resets that setting to its default.
 *
 * While the settings load the sheet shows placeholders, and if they could not be loaded it says so
 * with a way to retry. It used to be blank in both cases.
 */
import { useState } from 'react';
import { Settings } from 'lucide-react';
import { Button, Input, Label, Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle, SheetTrigger, Skeleton, Spinner } from '@design-system';
import type { Prospecting } from '@client/api/types';
import { useScoringSettings } from '../hooks/use-scoring-settings';
import { priorityCopy } from '../lib/labels';

/** An emptied box means "back to the default" (null); anything else not a number is ignored. */
function readNumber(value: string): number | null | undefined {
  if (value.trim() === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

interface ScoringSettingsSheetProps {
  provider: Prospecting.Provider;
}

export function ScoringSettingsSheet({ provider }: ScoringSettingsSheetProps) {
  const [open, setOpen] = useState(false);
  const settings = useScoringSettings(provider, open);
  const value = settings.value;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="outline" size="sm">
          <Settings />
          Settings
        </Button>
      </SheetTrigger>
      <SheetContent className="flex w-full flex-col gap-6 overflow-y-auto sm:max-w-md">
        <SheetHeader>
          <SheetTitle>Scoring settings</SheetTitle>
          <SheetDescription>Changes apply the next time your contacts are scored, usually within minutes.</SheetDescription>
        </SheetHeader>

        {settings.isLoading && (
          <div role="status" aria-label="Loading your settings" className="flex flex-col gap-5 px-4">
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton key={index} className="h-14 w-full" />
            ))}
          </div>
        )}
        {settings.loadError && (
          <div className="flex flex-col items-start gap-2 px-4 text-sm">
            <span className="text-destructive">Your settings could not be loaded. {settings.loadError}</span>
            <Button variant="outline" size="sm" onClick={settings.reload}>
              Try again
            </Button>
          </div>
        )}

        {value && settings.bounds && (
          <div className="flex flex-col gap-5 px-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="tier1CapPercent">{priorityCopy.settings.share}</Label>
              <Input
                id="tier1CapPercent"
                key={`percent-${value.tier1CapPercent}`}
                type="number"
                min={settings.bounds.tier1CapPercent.min * 100}
                max={settings.bounds.tier1CapPercent.max * 100}
                defaultValue={Math.round(value.tier1CapPercent * 1000) / 10}
                onBlur={event => {
                  const percent = readNumber(event.target.value);
                  if (percent !== undefined) settings.change({ tier1CapPercent: percent === null ? null : percent / 100 });
                }}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="tier1CapCount">{priorityCopy.settings.count}</Label>
              <Input
                id="tier1CapCount"
                key={`count-${value.tier1CapCount}`}
                type="number"
                min={settings.bounds.tier1CapCount.min}
                max={settings.bounds.tier1CapCount.max}
                defaultValue={value.tier1CapCount ?? ''}
                placeholder="No limit"
                onBlur={event => {
                  const count = readNumber(event.target.value);
                  if (count !== undefined) settings.change({ tier1CapCount: count });
                }}
              />
              <span className="text-xs text-gray-500">{priorityCopy.settings.hint}</span>
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="maxIssueAge">Maximum issue age</Label>
              <Input
                id="maxIssueAge"
                key={`age-${value.maxIssueAge}`}
                type="number"
                min={settings.bounds.maxIssueAge.min}
                max={settings.bounds.maxIssueAge.max}
                defaultValue={value.maxIssueAge}
                onBlur={event => {
                  const age = readNumber(event.target.value);
                  if (age !== undefined) settings.change({ maxIssueAge: age });
                }}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="restricted">Leave out contacts with these record types</Label>
              <Input
                id="restricted"
                placeholder="For example: Prospect"
                defaultValue={value.restrictedRecordTypeValues.join(', ')}
                onBlur={event =>
                  settings.change({
                    restrictedRecordTypeValues: event.target.value
                      .split(',')
                      .map(entry => entry.trim())
                      .filter(Boolean),
                  })
                }
              />
              <span className="text-xs text-gray-500">
                Only applies if a record type field is mapped. Separate values with commas.
              </span>
            </div>
            {settings.saveError && <span className="text-sm text-destructive">{settings.saveError}</span>}
          </div>
        )}

        <SheetFooter>
          <Button disabled={!settings.isDirty || settings.isSaving} onClick={settings.save}>
            {settings.isSaving ? (
              <>
                <Spinner aria-hidden="true" />
                Saving…
              </>
            ) : (
              'Save settings'
            )}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
